import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import useEmployeeMultiFilters from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { applyFilters } from 'modules/core/lib/filters'
import FiltersForm from 'modules/core/lib/forms/Filters'

import type Employee from 'modules/employees/store/models/Employee'
import SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type { RouteContextParams } from 'modules/payroll_bundle/lib/RouteContextParams'
import { INITIAL_PAGE, PAGINATION_PER_PAGE_SUPPLEMENTS } from 'modules/payroll_bundle/lib/constants'
import { useFrequency } from 'modules/payroll_bundle/lib/frequencyContext'
import { SupplementsInsightsContextProvider } from 'modules/payroll_bundle/pages/SupplementsPage/supplementsInsightsContext'

import getFilterValue from 'modules/payroll_bundle/components/Supplements/components/Table/_filters/getFilterValue'
import { ExpensablesProvider } from 'modules/payroll_bundle/components/Supplements/expensablesContext'
import { SupplementExtraDataProvider } from 'modules/payroll_bundle/components/Supplements/supplementExtraDataContext'
import useFetchData from 'modules/payroll_bundle/components/Supplements/useFetchData'
import SearchableSupplements from './SearchableSupplements'

export const SupplementsByDateAndFrequency = () => {
  const filtersForm = useLazyRef(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: 'activity___all',
      })
  )
  const { date, frequency } = useFrequency()
  const supplementsStore = useLazyRef<SupplementsFormStore>(
    () => new SupplementsFormStore()
  ).current

  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const { filteredEmployees } = useOutletContext<RouteContextParams>()

  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE)

  const { loading: loadingSortedEmployees, value: sortedEmployees } = useAsyncData(
    () =>
      applyFilters(filtersForm.current, filteredEmployees, {
        search: debouncedSearch,
      }),
    [
      filtersForm.current.get('filterBy').value,
      filtersForm.current.get('sortDirection').value,
      filtersForm.current.get('sortAttribute').value,
      filteredEmployees.length,
    ]
  )

  const employeeIds: number[] = sortedEmployees?.map((employee) => employee.id) ?? []

  const employeesInPeriodIds = filteredEmployees
    .map((employee) => employee.id)
    .sort((a, b) => a - b)
    .join('-')

  useEffect(() => {
    onApplyFilters(filteredEmployees)
  }, [employeesInPeriodIds]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    filteredModels: filteredEmployeesModels,
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, LOCAL_STORAGE_KEYS.supplementFilters)

  const [slicedEmployeeIds, setSlicedEmployeeIds] = useState<number[]>(
    employeeIds.slice(0, PAGINATION_PER_PAGE_SUPPLEMENTS)
  )

  const { loading, fetchSupplements } = useFetchData({
    date,
    frequency,
    supplementsStore,
    employeeIds: slicedEmployeeIds,
    currentPage,
  })

  useEffect(() => {
    const idsList = employeeIds.slice(
      PAGINATION_PER_PAGE_SUPPLEMENTS * (currentPage - 1),
      PAGINATION_PER_PAGE_SUPPLEMENTS * currentPage
    )
    setSlicedEmployeeIds(idsList)
    fetchSupplements(idsList)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, employeeIds.join('-')])

  return (
    <SupplementsInsightsContextProvider>
      <SupplementExtraDataProvider
        supplementsStore={supplementsStore}
        loadingSupplementData={loading}
      >
        <ExpensablesProvider supplementsStore={supplementsStore}>
          <SearchableSupplements
            store={supplementsStore}
            loading={loading || loadingSortedEmployees}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            filteredEmployees={sortedEmployees ?? []}
            onSearch={onSearch}
            search={search}
            filteredEmployeesModels={filteredEmployeesModels}
            logicOperator={logicOperator}
            filters={filters}
            onApplyFilters={onApplyFilters}
            onCancel={onCancel}
            onRemoveFilter={onRemoveFilter}
            onAddFilter={onAddFilter}
          />
        </ExpensablesProvider>
      </SupplementExtraDataProvider>
    </SupplementsInsightsContextProvider>
  )
}
